// Inline bordered-textarea editor for Rewrite / Insert paragraph
// actions. Lives inside SourceDocPane at the paragraph slot: a Rewrite
// REPLACES the paragraph with the editor, an Insert renders the editor
// AFTER the anchor paragraph. Same visual block in both cases.
//
// Shortcuts: ⌘↵ / Ctrl↵ saves, Esc cancels. Both propagate up through
// onSave / onCancel so the parent handles the resulting state change
// (clearing the editor + firing the SelectionAction).
//
// Design mirror: `.rwbox` block in gc-coach-map-restructure.html —
// 2px brand border, --bg background, footer with Save / Cancel / small
// note. We don't do the authored-tint on the editor itself; the tint
// kicks in once the override is saved and the paragraph re-renders
// with its `.paraRewritten` / `.paraInserted` class.

import { useEffect, useRef, useState } from 'react';
import styles from './ParagraphEditor.module.css';

export interface ParagraphEditorProps {
  mode: 'rewrite' | 'insert';
  /** For rewrite: the current paragraph text. For insert: empty string. */
  initialText: string;
  onSave: (text: string) => void;
  onCancel: () => void;
  t: (k: string) => string;
}

export function ParagraphEditor({
  mode,
  initialText,
  onSave,
  onCancel,
  t,
}: ParagraphEditorProps) {
  const [text, setText] = useState(initialText);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // Auto-focus on mount — the reviewer just picked an action, they
  // want to type immediately. Caret lands at the END for rewrite (so
  // they can edit from the natural insertion point) and at position 0
  // for insert (fresh content).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    if (mode === 'rewrite') {
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [mode]);

  const commit = () => {
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      // Save-of-empty is treated as cancel: inserting nothing is a no-op,
      // rewriting-to-empty would be an exclude in disguise and the brief
      // reserves Exclude for its own action.
      onCancel();
      return;
    }
    onSave(trimmed);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      commit();
      return;
    }
  };

  // onClick on the wrapper swallows propagation so the pane's
  // selectionchange handler doesn't dismiss the toolbar / editor the
  // moment the reviewer clicks inside the textarea to move the caret.
  return (
    <div className={styles.box} onClick={(e) => e.stopPropagation()}>
      <textarea
        ref={ref}
        className={styles.textarea}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        rows={4}
        placeholder={
          mode === 'insert' ? t('mapReview.rewriteInsertPlaceholder') : undefined
        }
        aria-label={
          mode === 'rewrite'
            ? t('mapReview.rewriteAriaRewrite')
            : t('mapReview.rewriteAriaInsert')
        }
      />
      <div className={styles.footer}>
        <button
          type="button"
          className={styles.primary}
          onClick={commit}
        >
          {t('mapReview.rewriteSave')}
          <kbd className={styles.kbd}>⌘↵</kbd>
        </button>
        <button type="button" className={styles.secondary} onClick={onCancel}>
          {t('mapReview.rewriteCancel')}
          <kbd className={styles.kbd}>Esc</kbd>
        </button>
        {mode === 'rewrite' && (
          <span className={styles.note}>
            {t('mapReview.rewriteOriginalKept')}
          </span>
        )}
      </div>
    </div>
  );
}
